import QRCode from "qrcode";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";

import type {
  Address,
  OrderDetail,
  OrderDocument,
  OrderPickup,
} from "@/api/types";
import {
  useAuthMe,
  useCreateStripeSession,
  useMyOrderShipments,
  useOrder,
  useOrderMaterialCertificates,
  useOrderPaymentInstructions,
  useSaveOrderAsProject,
} from "@/api/queries";
import { useTenantBranding } from "@/api/queries/branding";
import { useProcesses } from "@/api/queries/processes";
import type { OrderPaymentInstructions } from "@/api/types";
import { CarbonBadge } from "@/components/CarbonBadge";
import { OrderItemRow } from "@/components/order/OrderItemRow";
import { CustomerCancelOrder } from "@/components/orders/CustomerCancelOrder";
import { CustomerOrderClaims } from "@/components/orders/CustomerOrderClaims";
import { CustomerShipmentsCard } from "@/components/orders/CustomerShipmentsCard";
import { OrderDeliverySnapshot } from "@/components/orders/OrderDeliverySnapshot";
import {
  CustomerOrderTimeline,
  CustomerProductionProgressCard,
} from "@/components/order/CustomerOrderStatus";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { apiFetch, ApiError } from "@/lib/api";
import { countryName, formatPostalCity } from "@/lib/displayFormat";
import { pickupKey } from "@/lib/pickupVocabulary";
import { displayId } from "@/lib/identifiers";
import { minorToMajor } from "@/lib/money";
import { formatCalendarDate } from "@/lib/labels";
import { orderDeliveryLabel } from "@/lib/orderDisplay";
import { useSeo } from "@/lib/seo";
import { cx, formatMoney } from "@/lib/utils";
import { formatWeeklySchedule } from "@/lib/weeklySchedule";

// KAN-1438 — stavy, ve kterých je objednávka „čerstvá" a nadpis
// „Děkujeme!" ještě sedí: čeká na platbu, nebo je zaplacená a čeká na
// zahájení výroby (``paid`` je čekací sloupec).
const JUST_ORDERED_STATUSES = new Set(["pending_payment", "paid"]);

const SAVE_AS_PROJECT_STATUSES = new Set([
  "paid",
  "awaiting_material",
  "in_production",
  "awaiting_inbound_delivery",
  "ready_for_packing",
  "ready_to_ship",
  "shipped",
  "delivered",
  "refunded",
]);

/**
 * Post-checkout "thanks" page. Shows the order summary + a "Pay with
 * card" button when status is `pending_payment`. Clicking Pay creates
 * a Stripe Checkout Session via the backend and window.location's to
 * the returned URL. Stripe's success_url brings the user back here
 * with ?paid=1; the webhook flips status to `paid` out of band, so
 * the page refetches and shows the final state.
 *
 * KAN-764 - that card button used to be unconditional, so a shop with
 * no working Stripe account showed every customer a Pay button that
 * either failed (`connect_account_not_ready`) or charged through the
 * PLATFORM account instead of the shop's, while the only way to learn
 * how to actually pay was to open the proforma PDF. When
 * `shop_readiness.card_payments_ready` is positively false the page
 * now shows the transfer instructions instead of the button.
 *
 * KAN-993 - and the ORDER now answers that question itself. The shop
 * flag was a shop-wide verdict; since the customer picks a method at
 * checkout, two orders of the same shop can settle differently. The
 * flag stays as the fallback for orders placed before the column
 * existed (`payment_method === null`), which is exactly what it always
 * decided for them.
 */
export default function OrderThanks(): JSX.Element {
  const { t: tO } = useTranslation("order");
  const { t: tTrack } = useTranslation("track");
  const { orderNumber, lang: langParam } = useParams<{
    orderNumber: string;
    lang: string;
  }>();
  const [searchParams] = useSearchParams();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;
  const locale = lang === "cs" ? "cs-CZ" : "en-US";

  const order = useOrder(orderNumber ?? null, {
    lang,
    // Refetch more aggressively when we land back from Stripe with
    // ?paid=1 — the webhook may not have arrived yet.
    refetchInterval: searchParams.get("paid") === "1" ? 2_000 : false,
  });
  // Operation names for the delivery snapshot — slugs never reach the
  // customer (KAN-926).
  const processes = useProcesses({ includeNonEntry: true });
  const processName = (slug: string): string =>
    processes.data?.find((p) => p.slug === slug)?.display_name || slug;

  // KAN-1430 — titulek ukazuje číslo, které zákazník zná (xxx-xxx-xxx);
  // technické U-… zůstává jen v URL.
  const shownNumber = order.data ? displayId(order.data) : orderNumber;
  // KAN-1653 — titulek karty sleduje nadpis stránky: „Děkujeme!" jen
  // u čerstvé objednávky (KAN-1438), jinak „Objednávka <číslo>".
  // „Děkujeme!“ patří prvnímu zobrazení (návrat z platby). Zaplacená, ale
  // ještě nezahájená objednávka při pozdějším návratu nese běžný nadpis
  // s číslem (KAN-1796); čekající na platbu děkuje dál.
  const [firstVisit] = useState<boolean>(() => {
    try {
      const key = `forgmatic:thanks-seen:${orderNumber ?? ""}`;
      const seen = window.sessionStorage.getItem(key) !== null;
      window.sessionStorage.setItem(key, "1");
      return !seen;
    } catch {
      return true;
    }
  });
  const thanksFor = (status: string): boolean =>
    JUST_ORDERED_STATUSES.has(status) && (status === "pending_payment" || firstVisit);
  const justOrdered = !order.data || thanksFor(order.data.status);
  useSeo({
    title: !justOrdered && shownNumber
      ? tO("orderHeading", { number: shownNumber })
      : shownNumber
        ? `${tO("thanks")} · ${shownNumber}`
        : tO("thanks"),
    path: `/order/${orderNumber ?? ""}`,
    lang,
  });
  const me = useAuthMe({ retry: false });
  // Only a POSITIVE "this shop cannot take cards" switches the page to
  // the transfer path. While branding is still in flight the answer is
  // unknown, and telling a customer to transfer money to a shop that
  // does take cards is the worse of the two wrong guesses.
  const branding = useTenantBranding();
  const shopTakesNoCards =
    branding.isSuccess &&
    branding.data?.shop_readiness?.card_payments_ready === false;
  // Objednávka, která svůj způsob platby nese, rozhoduje sama; ta bez
  // něj (starší než KAN-993) padá zpátky na verdikt obchodu.
  const orderPaymentMethod = order.data?.payment_method ?? null;
  const transferOnly =
    orderPaymentMethod !== null
      ? orderPaymentMethod === "bank_transfer"
      : shopTakesNoCards;
  // KAN-1641 — zaplatit jde, jen když to server nabízí (zrušená
  // objednávka platbu ani pokyny k ní nedostane).
  const awaitingPayment = order.data?.available_actions?.includes("pay") ?? false;
  // Gated, not eager: the endpoint ISSUES the proforma the first time
  // it is asked, so it must not fire for orders that can be paid by
  // card or are already settled.
  const paymentInstructions = useOrderPaymentInstructions(orderNumber ?? null, {
    enabled: Boolean(awaitingPayment) && transferOnly,
  });
  const certs = useOrderMaterialCertificates(orderNumber ?? null);
  const shipments = useMyOrderShipments(orderNumber ?? null);
  const [savedProjectId, setSavedProjectId] = useState<string | null>(null);
  const createSession = useCreateStripeSession({
    onSuccess: (res) => {
      window.location.href = res.url;
    },
  });
  const saveProject = useSaveOrderAsProject({
    onSuccess: (project) => setSavedProjectId(project.public_id),
  });

  if (order.isLoading) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-16 text-center text-ink-3">
        …
      </main>
    );
  }

  if (order.error || !order.data) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-16 text-center">
        <p className="text-ink-2" data-testid="order-not-found">
          {tO("notFound")}
        </p>
        <Link
          to={`/${lang}`}
          className="mt-4 inline-block text-ink-3 underline"
        >
          {tO("backToHome")}
        </Link>
      </main>
    );
  }

  const o = order.data;
  const fxRate = o.fx_rate_to_czk;
  const canSaveAsProject = SAVE_AS_PROJECT_STATUSES.has(o.status);
  // The order's total, formatted once. The summary row and the transfer
  // instructions both quote it, and a customer must never be able to
  // read two different amounts for one order off one page.
  const totalFormatted = formatMoney(
    o.total_czk,
    o.currency,
    locale,
    fxRate,
  );
  // KAN-2189 — položky nesou cenu bez DPH, „Celkem" ji obsahuje. U plátce
  // (objednávka s DPH) bez označení četl zákazník „550 Kč" vedle „822 Kč".
  const chargesVat = (o.vat_czk ?? 0) > 0;

  return (
    <main className="mx-auto max-w-3xl px-6 py-8">
      <section className="mt-10 text-center">
        {/* KAN-1438 — táž stránka je i detailem objednávky, na který se
            zákazník vrací z e-mailů týdny po nákupu. „Děkujeme!" patří
            jen k čerstvé objednávce; u rozpracované nebo odeslané by
            nadpis mluvil o něčem, co už dávno proběhlo. */}
        {thanksFor(o.status) ? (
          <>
            <h1 className="text-4xl font-bold tracking-tight text-ink" data-testid="order-heading">
              {tO("thanks")}
            </h1>
            <p className="mt-2 text-ink-2">{tO("thanksBody")}</p>
          </>
        ) : (
          <h1 className="text-4xl font-bold tracking-tight text-ink" data-testid="order-heading">
            {tO("orderHeading", { number: displayId(o) })}
          </h1>
        )}
      </section>

      {/* KAN-1908 — objednávka nad schvalovacím limitem firmy čeká na
          správce firemního účtu, ne na platbu; stránka to dřív neříkala
          a zákazník nevěděl, na koho čeká. */}
      {o.status === "pending_approval" && (
        <section
          className="mt-6 rounded-md border border-border bg-surface-2 p-4 text-sm text-ink-2"
          data-testid="order-awaiting-approval"
        >
          <p className="font-medium text-ink">{tO("awaitingApproval.title")}</p>
          <p className="mt-1">{tO("awaitingApproval.body")}</p>
        </section>
      )}

      {o.production_progress && (
        <CustomerProductionProgressCard progress={o.production_progress} />
      )}
      <CustomerOrderTimeline timeline={o.timeline ?? []} locale={locale} pickup={Boolean(o.pickup)} />

      {shipments.data && shipments.data.shipments.length > 0 && (
        <CustomerShipmentsCard
          shipments={shipments.data.shipments}
          fulfillment={shipments.data.fulfillment}
          dispatch={shipments.data.dispatch}
          locale={locale}
          pickup={Boolean(o.pickup) || shipments.data.personal_pickup === true}
        />
      )}

      {searchParams.get("paid") === "1" && o.status === "paid" && (
        <section
          className="mt-6 rounded-md border border-ok-border bg-ok-bg p-4 text-center text-ok"
          role="status"
          data-testid="order-paid-notice"
        >
          {tO("paidConfirmation")}
        </section>
      )}
      {searchParams.get("cancelled") === "1" && o.status !== "paid" && (
        <section
          className="mt-6 rounded-md border border-warn-border bg-warn-bg p-4 text-center text-warn"
          role="status"
          data-testid="order-cancelled-notice"
        >
          {tO("cancelledNotice")}
        </section>
      )}

      {canSaveAsProject && (
        <section
          className="mt-6 rounded-md border border-border bg-surface p-4"
          data-testid="save-order-project"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-semibold text-ink">
                {tO("saveProject.title")}
              </h2>
              <p className="mt-1 text-sm text-ink-2">
                {me.data ? tO("saveProject.body") : tO("saveProject.signIn")}
              </p>
            </div>
            {savedProjectId ? (
              <Link
                to={`/${lang}/account/projects/${savedProjectId}`}
                className="inline-flex justify-center rounded-md border border-border-strong bg-surface px-3 py-2 text-sm font-medium text-ink hover:bg-bg"
                data-testid="open-saved-project"
              >
                {tO("saveProject.open")}
              </Link>
            ) : me.data ? (
              <button
                type="button"
                disabled={saveProject.isPending}
                onClick={() =>
                  saveProject.mutate({
                    orderNumber: o.order_number,
                    name: `${tO("orderNumber")} ${displayId(o)}`,
                  })
                }
                className="rounded-md bg-ink px-3 py-2 text-sm font-medium text-bg disabled:opacity-60"
                data-testid="save-as-project"
              >
                {saveProject.isPending
                  ? tO("saveProject.saving")
                  : tO("saveProject.action")}
              </button>
            ) : (
              <Link
                to={`/${lang}/sign-in?redirect_to=${encodeURIComponent(`/${lang}/order/${displayId(o)}`)}`}
                className="inline-flex justify-center rounded-md bg-ink px-3 py-2 text-sm font-medium text-bg"
              >
                {tO("saveProject.action")}
              </Link>
            )}
          </div>
          {savedProjectId && (
            <p className="mt-2 text-sm text-ok">{tO("saveProject.saved")}</p>
          )}
          {/* KAN-1899 — neúspěch uložení se dřív neukázal vůbec: tlačítko
              se jen vrátilo do klidu a zákazník nevěděl, že se nic nestalo. */}
          {!savedProjectId && saveProject.isError && (
            <p
              className="mt-2 text-sm text-danger"
              role="alert"
              data-testid="save-as-project-error"
            >
              {tO("saveProject.error")}
            </p>
          )}
        </section>
      )}

      <section
        className="mt-10 space-y-4 rounded-lg border border-border bg-surface p-5"
        data-testid="order-confirmation"
      >
        <div className="flex justify-between">
          <span className="text-ink-3">{tO("orderNumber")}</span>
          <span className="font-mono text-ink" data-testid="order-number">
            {displayId(o)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-ink-3">{tO("status")}</span>
          {/* ``data-status`` nese SUROVÝ stav vedle přeloženého textu:
              e2e jinak musí porovnávat řetězec, který se mění s jazykem
              i s copy — přesně to, čemu se testid vyhýbá. */}
          <span
            className="text-ink"
            data-testid="order-status"
            data-status={o.status}
          >
            {/* KAN-1438 — úplná zákaznická mapa stavů (táž jako /track);
                dřív se mimo dva stavy ukázal syrový kód stavu. */}
            {/* KAN-2015 — u osobního odběru „Připraveno k vyzvednutí" / „Vydáno". */}
            {tTrack(pickupKey(`status.${o.status}`, Boolean(o.pickup), "track"), {
              defaultValue: o.status,
            })}
          </span>
        </div>
        {o.delivery_code && (
          <div className="flex justify-between" data-testid="order-delivery">
            <span className="text-ink-3">{tO("delivery")}</span>
            <span className="text-ink">
              {/* KAN-926: the code is a tenant tier CODE („standardni"),
                  the name lives in the frozen snapshot; the i18n keys
                  only cover legacy orders without one. */}
              {orderDeliveryLabel(o.delivery_selection, o.delivery_code, (code) =>
                tO(`delivery${capitalize(code)}`, { defaultValue: "" }),
              )}
            </span>
          </div>
        )}
        {/* KAN-2100 — číslo objednávky odběratele (zadané v pokladně nebo
            doplněné obchodem) patří i na potvrzení, ne jen na fakturu. */}
        {o.customer_reference && (
          <div className="flex justify-between" data-testid="order-customer-reference">
            <span className="text-ink-3">{tO("customerReference")}</span>
            <span className="font-mono text-ink">{o.customer_reference}</span>
          </div>
        )}
        {/* KAN-2111 — IČO z pokladny a nabídka, ze které objednávka vznikla. */}
        {o.ico && (
          <div className="flex justify-between" data-testid="order-buyer-ico">
            <span className="text-ink-3">{tO("buyerIco")}</span>
            <span className="font-mono text-ink">{o.ico}</span>
          </div>
        )}
        {o.quote_public_id && (
          <div className="flex justify-between" data-testid="order-source-quote">
            <span className="text-ink-3">{tO("sourceQuote")}</span>
            <Link
              to={`/${lang}/quotes/by-id/${encodeURIComponent(o.quote_public_id)}`}
              className="text-ink underline"
            >
              {tO("sourceQuoteLink")}
            </Link>
          </div>
        )}
        {o.delivery_selection && (
          <OrderDeliverySnapshot
            selection={o.delivery_selection}
            formatProcessType={processName}
            showHeader={!o.delivery_code}
          />
        )}
        {o.estimated_production_complete_at && (
          <div className="flex justify-between">
            <span className="text-ink-3">
              {tO("estimatedProductionComplete")}
            </span>
            <span className="text-ink">
              {formatCalendarDate(
                o.estimated_production_complete_at,
                locale,
                o.shop_timezone,
              )}
            </span>
          </div>
        )}
        {o.estimated_delivery_at && (
          <div className="flex justify-between">
            <span className="text-ink-3">{tO(pickupKey("estimatedDelivery", Boolean(o.pickup), "customer"))}</span>
            <span className="text-ink">
              {formatCalendarDate(
                o.estimated_delivery_at,
                locale,
                o.shop_timezone,
              )}
            </span>
          </div>
        )}
        {/* KAN-269 — viz Track.tsx: krok bez odhadu doby znamená, že
            závazný termín neexistuje. Prázdno by tu vypadalo jako chyba. */}
        {o.eta_pending_estimate && (
          <div className="flex justify-between">
            <span className="text-ink-3">{tO(pickupKey("estimatedDelivery", Boolean(o.pickup), "customer"))}</span>
            <span className="text-ink" title={tO("etaPendingEstimateHint")}>
              {tO("etaPendingEstimate")}
            </span>
          </div>
        )}
        <div className="flex items-baseline justify-between border-t border-border pt-3">
          <span className="text-lg font-semibold text-ink-2">
            {/* KAN-2189 — úhrn obsahuje DPH, položky výš ne: obě to musí říct. */}
            {tO(chargesVat ? "totalGross" : "total")}
          </span>
          <span className="text-2xl font-bold tabular-nums text-ink">
            {totalFormatted}
          </span>
        </div>

        {o.carbon_footprint_gco2e !== null &&
          o.carbon_footprint_gco2e !== undefined && (
            <div className="flex justify-end">
              <CarbonBadge gco2e={o.carbon_footprint_gco2e} />
            </div>
          )}

        {awaitingPayment &&
          (transferOnly ? (
            <TransferInstructions
              instructions={paymentInstructions.data}
              failed={paymentInstructions.isError}
              loading={paymentInstructions.isLoading}
              amount={totalFormatted}
            />
          ) : (
            <div className="space-y-2 border-t border-border pt-3">
              <button
                type="button"
                disabled={createSession.isPending}
                onClick={() => createSession.mutate(o.order_number)}
                className={cx(
                  "w-full rounded-md bg-ink px-4 py-2.5 text-sm font-medium text-bg transition",
                  "hover:bg-ink",
                  "disabled:cursor-not-allowed disabled:opacity-60",
                )}
                data-testid="pay-now"
              >
                {createSession.isPending ? tO("paying") : tO("payNow")}
              </button>
              {createSession.error && (
                <p className="text-sm text-danger" role="alert">
                  {tO("payError")}
                </p>
              )}
            </div>
          ))}

        <CustomerCancelOrder
          orderNumber={o.order_number}
          actions={o.available_actions ?? []}
          lang={lang}
          status={o.status}
        />

        {o.status !== "draft" && (
          <CustomerOrderClaims
            orderNumber={o.order_number}
            actions={o.available_actions ?? []}
            items={o.items}
          />
        )}

        {o.status !== "draft" && (
          <div className="flex flex-wrap items-center gap-4 border-t border-border pt-3">
            {/* KAN-1489 — jeden doklad podle stavu platby, ne oba naráz.
                Před platbou existuje zálohová faktura, ostrý daňový doklad
                vzniká až zaplacením (a endpoint ho na nezaplacené
                objednávce odmítne 409). Nabízet oba znamenalo, že zákazník
                dostal ostrou fakturu „Nezaplaceno" ještě před platbou. */}
            {/* KAN-1551 — obchod, který doklady vystavuje jinde, je sem
                dodá později. Do té doby odkaz nevede na odmítnutí, jen
                řekne, že doklad se připravuje. */}
            {o.paid_at ? (
              o.invoice_available !== false ? (
                <a
                  href={`/api/v1/orders/${o.order_number}/invoice.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-ink-2 underline hover:text-ink"
                  data-testid="download-invoice"
                >
                  {tO("downloadInvoice")}
                </a>
              ) : (
                <span
                  className="text-sm text-ink-3"
                  data-testid="invoice-pending"
                >
                  {tO("invoicePending")}
                </span>
              )
            ) : (
              o.proforma_available !== false && (
                <a
                  href={`/api/v1/orders/${o.order_number}/proforma.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-ink-2 underline hover:text-ink"
                  data-testid="download-proforma"
                >
                  {tO("downloadProforma")}
                </a>
              )
            )}
            {extraDocuments(o).map((doc) => (
              <a
                key={doc.id}
                href={`/api/v1/orders/${o.order_number}/documents/${doc.id}.pdf`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm text-ink-2 underline hover:text-ink"
                data-testid={`download-document-${doc.id}`}
              >
                {tO(DOCUMENT_LABEL_KEYS[doc.type], { number: doc.number ?? "" })}
              </a>
            ))}
            {certs.data?.items.map((cert) => (
              <a
                key={`${cert.material_code}-${cert.batch_number}-${cert.certificate_kind}`}
                href={cert.download_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm text-ink-2 underline hover:text-ink"
              >
                {tO("downloadMaterialCertificate", {
                  material: cert.material_display_name || cert.material_code,
                  batch: cert.batch_number,
                  kind: cert.certificate_kind,
                })}
              </a>
            ))}
            {/* KAN-2020 — „Poslat fakturu znovu" jen když zákazník nějakou
                fakturu UŽ dostal: před zaplacením existuje jen zálohová
                (stahuje se výš) a „znovu" by slibovalo doklad, který nepřišel. */}
            {(o.available_actions ?? []).includes("resend_invoice") &&
              !!o.paid_at &&
              o.invoice_available !== false && (
                <ResendInvoiceButton orderNumber={o.order_number} />
              )}
          </div>
        )}

        {/* Znění, které kupující přijal — s odkazem na TU verzi, ne na
            dnešní. Bez ``?v=`` by se po první změně podmínek dostal jen
            k novým a doklad by mu byl k ničemu. KAN-2020 — číslo verze
            („v2", „v3") se nevypisuje: u obchodu starého hodinu nedává
            zákazníkovi smysl; přesné znění drží odkaz. */}
        {(o.legal_acceptances?.length ?? 0) > 0 && (
          <div
            className="mt-4 border-t border-border pt-4"
            data-testid="order-legal-acceptances"
          >
            <p className="text-xs text-ink-3">
              {tO(
                "legalAcceptedHeading",
                "Při objednávce jste odsouhlasili tato znění:",
              )}
            </p>
            <ul className="mt-1 space-y-1">
              {o.legal_acceptances?.map((doc) => (
                <li key={doc.kind_key} className="text-sm text-ink-2">
                  {doc.slug ? (
                    <Link
                      to={`/${lang}/${doc.slug}?v=${encodeURIComponent(doc.version)}`}
                      className="underline"
                      data-testid={`order-legal-${doc.kind_key}`}
                    >
                      {doc.title}
                    </Link>
                  ) : (
                    <span>{doc.title}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="mt-8">
        <h2 className="mb-3 text-lg font-semibold text-ink">
          {tO(chargesVat ? "itemsNet" : "items")}
        </h2>
        <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
          {o.items.map((item, i) => (
            <OrderItemRow
              key={i}
              item={item}
              locale={locale}
              currency={o.currency}
              fxRate={fxRate}
            />
          ))}
          {/* KAN-2099 — příplatek za zvolenou úroveň termínu jako vlastní
              řádek; položky výš nesou cenu výroby z konfigurátoru. */}
          {o.delivery_surcharge && (
            <li
              className="flex justify-between gap-4 p-4 text-sm text-ink-2"
              data-testid="order-delivery-surcharge"
            >
              <span>
                {tO("deliverySurcharge", { name: o.delivery_surcharge.label })}
              </span>
              <span className="tabular-nums text-ink">
                {formatMoney(
                  o.delivery_surcharge.amount_base_major,
                  o.currency,
                  locale,
                  fxRate,
                )}
              </span>
            </li>
          )}
        </ul>
      </section>

      {(o.pickup || o.shipping_address) && (
        <section className="mt-8 grid gap-6 md:grid-cols-2">
          {/* KAN-1490: u osobního odběru se nikam nedoručuje, takže
              „Dodací adresa" je lež — zákazník potřebuje vědět, kam si
              přijít. Jeho vlastní adresa zůstává vedle jako kontakt:
              pokladna ji vybrala a na dokladu je. */}
          {o.pickup ? (
            <PickupCard pickup={o.pickup} lang={lang} />
          ) : (
            o.shipping_address && (
              <AddressCard
                title={tO("shippingAddress")}
                address={o.shipping_address}
              />
            )
          )}
          {o.pickup && o.shipping_address && (
            <AddressCard
              title={tO("pickup.contactHeading")}
              address={o.shipping_address}
            />
          )}
          {o.billing_address && (
            <AddressCard
              title={tO("billingAddress")}
              address={o.billing_address}
            />
          )}
        </section>
      )}
    </main>
  );
}

/**
 * What a shop without card payments needs the customer to see
 * (KAN-764): where to send the money, and under which reference.
 *
 * Every number is server-derived from the order's proforma, so this
 * block and the PDF cannot disagree about the reference. When the
 * fetch fails or the shop never filled its account in, it says the
 * shop will be in touch rather than inventing an account - an
 * invented payment instruction is worse than none.
 */

const DOCUMENT_LABEL_KEYS: Record<OrderDocument["type"], string> = {
  invoice: "documentInvoice",
  proforma: "documentProforma",
  credit_note: "documentCreditNote",
};

/**
 * KAN-1550 — documents beyond the one the primary link above serves:
 * further partial invoices, credit notes, a summary invoice's siblings.
 * The newest document of the primary type is already behind that link,
 * and a paid order does not offer its proforma any more (KAN-1489).
 */
function extraDocuments(o: OrderDetail): OrderDocument[] {
  const documents = o.documents ?? [];
  const primaryType = o.paid_at ? "invoice" : "proforma";
  const primary = documents.find((doc) => doc.type === primaryType);
  return documents.filter(
    (doc) =>
      doc.id !== primary?.id && !(o.paid_at && doc.type === "proforma"),
  );
}

function TransferInstructions({
  instructions,
  failed,
  loading = false,
  amount,
}: {
  instructions: OrderPaymentInstructions | undefined;
  failed: boolean;
  /** KAN-2189 — pokyny se teprve načítají (vystavuje se proforma); do té
   *  doby nesmí stránka tvrdit, že obchod platební údaje nemá. */
  loading?: boolean;
  amount: string;
}): JSX.Element {
  const { t: tO, i18n } = useTranslation("order");
  const account = instructions?.account ?? null;
  // KAN-1991 — část už dorazila: zákazník posílá jen zbytek (měna knih,
  // tatáž, kterou uvádí e-mail s výzvou k doplacení).
  const currency = instructions?.currency_code ?? null;
  const partly =
    currency !== null &&
    instructions?.received_minor != null &&
    instructions?.remaining_minor != null;
  const locale = i18n.language === "cs" ? "cs-CZ" : "en-US";
  const received = partly
    ? formatMoney(minorToMajor(instructions?.received_minor, currency), currency, locale, 1)
    : null;
  const toPay = partly
    ? formatMoney(minorToMajor(instructions?.remaining_minor, currency), currency, locale, 1)
    : amount;

  return (
    <div
      className="space-y-3 border-t border-border pt-3"
      data-testid="transfer-instructions"
    >
      <div>
        <h2 className="text-sm font-semibold text-ink">
          {tO("transfer.heading")}
        </h2>
        {/* KAN-1785 — „částku na účet níže" jen když ten účet dole je. Bez
            něj by dvě věty za sebou tvrdily opak (účet níže × obchod nemá
            číslo účtu), takže vede jediná věta z rámečku níž. */}
        {account ? (
          <p className="mt-1 text-sm text-ink-2" data-testid="transfer-lead">
            {tO("transfer.lead")}
          </p>
        ) : null}
      </div>

      {account ? (
        <dl className="space-y-2 rounded-md border border-border bg-bg p-4">
          <TransferRow label={tO("transfer.account")} value={account} mono />
          <TransferRow
            label={tO("transfer.variableSymbol")}
            value={instructions?.variable_symbol ?? ""}
            mono
          />
          {received ? (
            <TransferRow label={tO("transfer.received")} value={received} mono />
          ) : null}
          <TransferRow
            label={tO(partly ? "transfer.remaining" : "transfer.amount")}
            value={toPay}
            mono
          />
          <TransferRow
            label={tO("transfer.beneficiary")}
            value={instructions?.beneficiary ?? ""}
          />
          {instructions?.qr_payload ? (
            <PaymentQr payload={instructions.qr_payload} label={tO("transfer.qr")} />
          ) : null}
        </dl>
      ) : loading ? (
        <p className="text-sm text-ink-3" role="status" data-testid="transfer-instructions-loading">
          {tO("transfer.loading")}
        </p>
      ) : (
        <p
          className="rounded-md border border-warn-border bg-warn-bg p-4 text-sm text-warn"
          data-testid="transfer-instructions-unavailable"
        >
          {failed ? tO("transfer.failed") : tO("transfer.noAccount")}
        </p>
      )}
    </div>
  );
}

/**
 * KAN-2013 — QR platba (Český standard QR Platba / SPAYD). Obsah posílá
 * server (``qr_payload``, tentýž výpočet jako QR na zálohové faktuře);
 * stránka ho jen vykreslí. Nepodaří-li se, QR prostě chybí — účet a VS
 * nad ním zůstávají.
 */
function PaymentQr({ payload, label }: { payload: string; label: string }): JSX.Element | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(payload, { errorCorrectionLevel: "M", margin: 1, width: 168 })
      .then((url) => !cancelled && setSrc(url))
      .catch(() => !cancelled && setSrc(null));
    return () => {
      cancelled = true;
    };
  }, [payload]);
  if (!src) return null;
  return (
    <div className="flex items-center gap-3 pt-2" data-testid="transfer-qr">
      <img src={src} alt={label} width={168} height={168} className="rounded bg-white p-1" />
      <span className="text-sm text-ink-2">{label}</span>
    </div>
  );
}

/** One instruction line. ``mono`` for anything meant to be copied. */
function TransferRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <dt className="text-sm text-ink-3">{label}</dt>
      <dd className={cx("text-sm text-ink", mono && "font-mono")}>{value}</dd>
    </div>
  );
}

/**
 * Uppercase the first letter so delivery codes (``standard``,
 * ``express``, ``rush``) line up with the i18n keys
 * (``deliveryStandard`` / ``deliveryExpress`` / ``deliveryRush``).
 * Inlined rather than imported from utils — this is the only place
 * in the app that needs it and it's literally three characters.
 */
function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Kam a kdy si zákazník přijde (KAN-1490).
 *
 * Nahrazuje kartu „Dodací adresa" u objednávek s osobním odběrem —
 * ta u nich vypisovala adresu, na kterou nikdo nic nepošle, a nikde
 * neřekla ani to, že jde o odběr.
 *
 * Chybějící adresu ani hodiny si nevymýšlí: řekne, že je obchod
 * neuvedl, a že se ozve. Vymyšlené místo odběru je horší než žádné.
 */
function PickupCard({
  pickup,
  lang,
}: {
  pickup: OrderPickup;
  lang: string;
}): JSX.Element {
  const { t: tO } = useTranslation("order");
  // Rozvrh má přednost; volný text jen dokud rozvrh nic neotevírá —
  // táž přednost jako na veřejném profilu obchodu.
  const hours =
    formatWeeklySchedule(pickup.schedule, lang) ?? pickup.hours ?? null;

  return (
    <div
      className="rounded-lg border border-border bg-surface p-5"
      data-testid="order-pickup-card"
    >
      <h3 className="mb-2 text-sm font-medium text-ink-3">
        {tO("pickup.heading")}
      </h3>
      <p className="mb-3 text-sm text-ink-2">{tO("pickup.lead")}</p>
      <dl className="space-y-2 text-sm">
        <div>
          <dt className="text-ink-3">{tO("pickup.addressLabel")}</dt>
          <dd className={pickup.address ? "text-ink" : "text-ink-3"}>
            {pickup.address ?? tO("pickup.addressMissing")}
          </dd>
        </div>
        <div>
          <dt className="text-ink-3">{tO("pickup.hoursLabel")}</dt>
          <dd className={hours ? "text-ink" : "text-ink-3"}>
            {hours ?? tO("pickup.hoursMissing")}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function AddressCard({
  title,
  address,
}: {
  title: string;
  address: Address;
}): JSX.Element {
  // Compose the display name from the post-2026-04-24 split, or fall
  // back to the legacy single ``name`` field for orders stored before
  // the cutover. Empty on both sides would render no line.
  const first = (address.first_name ?? "").trim();
  const last = (address.last_name ?? "").trim();
  const composed = `${first} ${last}`.trim();
  const displayName = composed || (address.name ?? "").trim();
  const { i18n } = useTranslation();

  // KAN-2017 — PSČ ve tvaru země („110 00"), země lidským názvem
  // („Česko"), ne kódem „CZ".
  const lines = [
    displayName,
    address.company,
    address.street,
    formatPostalCity(address),
    countryName(address.country_code, i18n.language),
    address.phone,
    address.email,
  ].filter((x): x is string => typeof x === "string" && x.length > 0);

  return (
    <div className="rounded-lg border border-border bg-surface p-5">
      <h3 className="mb-2 text-sm font-medium text-ink-3">{title}</h3>
      {lines.map((line, i) => (
        <div key={i} className="text-sm text-ink">
          {line}
        </div>
      ))}
    </div>
  );
}

/**
 * "Resend invoice" button — calls POST /orders/{n}/resend-invoice.
 *
 * The button only shows for non-draft orders (the parent guards
 * status !== "draft"). Anonymous viewers must supply an e-mail; we
 * collect it via a tiny inline prompt rather than building a modal.
 *
 * Owners (signed-in user_id matches the order) call without a body
 * and the BE reads the address off the order. The handler returns
 * 404 if the e-mail doesn't match — same gate as ``/track``.
 */
function ResendInvoiceButton({
  orderNumber,
}: {
  orderNumber: string;
}): JSX.Element {
  const { t: tO } = useTranslation("order");
  const [state, setState] = useState<"idle" | "loading" | "sent" | "error">(
    "idle",
  );

  async function onClick() {
    setState("loading");
    // Try the owner path first (no body). On 400 / 404 we'd need an
    // e-mail prompt. The BE returns 400 ``email_required`` for
    // anonymous callers — we surface a quick window.prompt then.
    try {
      await apiFetch<{ sent_to: string }>(
        `/api/v1/orders/${encodeURIComponent(orderNumber)}/resend-invoice`,
        { method: "POST", body: {} },
      );
      setState("sent");
    } catch (err) {
      if (
        err instanceof ApiError &&
        (err.code === "email_required" || err.status === 400)
      ) {
        const email = window.prompt(tO("resendInvoice.promptEmail"));
        if (!email) {
          setState("idle");
          return;
        }
        try {
          await apiFetch<{ sent_to: string }>(
            `/api/v1/orders/${encodeURIComponent(orderNumber)}/resend-invoice`,
            { method: "POST", body: { email } },
          );
          setState("sent");
          return;
        } catch {
          setState("error");
          return;
        }
      }
      setState("error");
    }
  }

  if (state === "sent") {
    return (
      <span className="text-sm text-ok" data-testid="resend-invoice-sent">
        {tO("resendInvoice.sent")}
      </span>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={state === "loading"}
        className="text-sm text-ink-2 underline hover:text-ink disabled:opacity-50"
        data-testid="resend-invoice"
      >
        {state === "loading"
          ? tO("resendInvoice.sending")
          : tO("resendInvoice.action")}
      </button>
      {state === "error" && (
        <span
          className="text-sm text-danger"
          data-testid="resend-invoice-error"
        >
          {tO("resendInvoice.error")}
        </span>
      )}
    </>
  );
}
